import { AnalyticsBrowser, type EventProperties, GroupTraits, type UserTraits } from "@segment/analytics-next";

import config from "@/config";
import { isAnalyticsConsentGranted } from "@/helpers/consent";
import log from "@/helpers/log";

export class Analytics {
  private segment?: AnalyticsBrowser;

  private namespace?: string;

  private writeKey?: string;

  private globalProperties: Record<string, unknown> = {};

  public init(namespace: string, writeKey: string = config.segmentWriteKey): void {
    this.namespace = namespace;
    this.writeKey = writeKey;
  }

  public setGlobalProperties(properties: Record<string, unknown>) {
    this.globalProperties = { ...this.globalProperties, ...properties };
  }

  public setAnonymousId(anonymousId: string): void {
    if (this.isSkipped()) return;
    this.ensureLoaded();
    if (!this.segment) return;
    try {
      void this.segment.setAnonymousId(anonymousId);
    } catch (error) {
      log.error("Failed to set anonymous id in analytics", error);
    }
  }

  public async identify(userId: string, traits?: UserTraits): Promise<void> {
    if (this.isSkipped()) return;
    this.ensureLoaded();
    try {
      await this.getSegment().identify(userId, {
        ...traits,
      });
    } catch (error) {
      log.error(`Failed to identify user ${userId} in analytics`, error);
    }
  }

  public async group(groupId: string, traits?: GroupTraits): Promise<void> {
    if (this.isSkipped()) return;
    this.ensureLoaded();
    try {
      await this.getSegment().group(groupId, {
        ...traits,
      });
    } catch (error) {
      log.error(`Failed to group user ${groupId} in analytics`, error);
    }
  }

  public async track(event: string, properties?: EventProperties): Promise<void> {
    if (this.isSkipped()) return;
    this.ensureLoaded();
    try {
      await this.getSegment()?.track(event, {
        ...properties,
        ...this.globalProperties,
      });
    } catch (error) {
      log.error(`Failed to track event ${event}`, error);
    }
  }

  private ensureLoaded(): void {
    if (this.isSkipped()) {
      return;
    }
    if (this.segment) {
      return;
    }
    if (!this.writeKey) {
      log.error("Segment write key is not available");
      return;
    }
    if (!this.namespace) {
      log.error("Analytics namespace is not set");
      return;
    }

    this.segment = new AnalyticsBrowser();
    this.segment
      .load(
        { writeKey: this.writeKey },
        {
          user: {
            cookie: { key: `${this.namespace}_ajs_user_id` },
            localStorage: { key: `${this.namespace}_ajs_user_traits` },
          },
          globalAnalyticsKey: `${this.namespace}_analytics`,
        }
      )
      .then(() => {
        log.debug("Analytics initialized");
        return true;
      })
      .catch((error) => {
        log.error("Failed to initialize Analytics", error);
      });
  }

  private getSegment() {
    if (!this.segment) {
      throw new Error("Analytics not initialized. Call Analytics.init() first.");
    }
    return this.segment;
  }

  private isSkipped() {
    const dappOrigin = window.location.origin;

    // skip if the protocol is not http or https
    if (dappOrigin.startsWith("http://")) {
      return true;
    }
    // skip if dapp contains localhost
    if (dappOrigin.includes("localhost")) {
      return true;
    }
    // skip while Osano analytics consent is denied (allowed by default until actioned)
    if (!isAnalyticsConsentGranted()) {
      return true;
    }

    return false;
  }
}

// Project Analytics
const projectAnalytics = new Analytics();
projectAnalytics.setGlobalProperties({
  dashboard_build_env: config.buildEnv,
});
projectAnalytics.init("project");

// Organization Analytics
const orgAnalytics = new Analytics();
orgAnalytics.setGlobalProperties({
  dashboard_build_env: config.buildEnv,
});
orgAnalytics.init("organization");

// Agentic Analytics
const agenticAnalytics = new Analytics();
agenticAnalytics.setGlobalProperties({
  dashboard_build_env: config.buildEnv,
  product: "agentic",
  category: "agentic_dashboard",
});
agenticAnalytics.init("agentic", config.agenticSegmentWriteKey);

export { agenticAnalytics, orgAnalytics, projectAnalytics };

// CONSTANTS
export const PROJECT_ANALYTICS_EVENTS = {
  PROJECT_CREATED: "Project Created",
  PROJECT_UPDATED: "Project Updated",
  PROJECT_MOVED: "Project Moved",
  PROJECT_ARCHIVED: "Project Archived",
  PROJECT_RESTORED: "Project Restored",
  // General Settings
  PROJECT_GENERAL_SETTINGS_UPDATED: "Project General Settings Updated",
  PROJECT_DOMAIN_SETTINGS_UPDATED: "Project Domain Settings Updated",
  PROJECT_SESSION_DURATION_UPDATED: "Project Session Duration Updated",
  PROJECT_USER_DATA_IN_ID_TOKEN_TOGGLED: "Project User Data in ID Token Toggled",
  PROJECT_KEY_EXPORT_TOGGLED: "Project Key Export Toggled",
  // Chains and Networks
  PROJECT_NETWORK_TOGGLED: "Project Network Toggled",
  PROJECT_NETWORK_UPDATED: "Project Network Updated",
  PROJECT_CUSTOM_NETWORK_ADDED: "Project Custom Network Added",
  PROJECT_ALL_NETWORKS_TOGGLED: "Project All Networks Toggled",
  // Authentication
  PROJECT_SOCIAL_CONNECTION_TOGGLED: "Project Social Connection Toggled",
  PROJECT_ALL_SOCIAL_LOGINS_TOGGLED: "Project All Social Logins Toggled",
  PROJECT_EXTERNAL_WALLETS_TOGGLED: "Project External Wallets Toggled",
  PROJECT_EXTERNAL_WALLETS_UPDATED: "Project External Wallets Updated",
  PROJECT_CONNECTION_ADDED: "Project Connection Added",
  PROJECT_CONNECTION_UPDATED: "Project Connection Updated",
  PROJECT_CONNECTION_ARCHIVED: "Project Connection Archived",
  PROJECT_CONNECTION_RESTORED: "Project Connection Restored",
  // Smart Accounts
  PROJECT_SMART_ACCOUNT_NETWORK_UPDATED: "Project Smart Account Network Updated",
  PROJECT_SMART_ACCOUNT_UPDATED: "Project Smart Account Updated",
  // Wallet UI
  PROJECT_WALLET_UI_UPDATED: "Project Wallet UI Updated",
  // Customization
  PROJECT_BRANDING_UPDATED: "Project Branding Updated",
  PROJECT_LOGIN_MODAL_UPDATED: "Project Login Modal Updated",
  // CTA
  CTA_CLICKED: "CTA Clicked",
};

export const ANALYTICS_CTA_TYPES = {
  UPGRADE_PLAN: "upgrade-plan",
  DOWNGRADE_PLAN: "downgrade-plan",
  DOCS_REFERENCE: "docs-reference",
};

export const ANALYTICS_USER_TYPES = {
  ORGANIZATION: "organization",
  PROJECT: "project",
};

/** Router history-state key for LOGIN_COMPLETED payload across End → AGENTIC_ENTRY (not in the URL). */
export const AGENTIC_LOGIN_COMPLETED_STATE_KEY = "agenticLoginCompleted";

/** Deferred LOGIN_COMPLETED event properties passed via router history state. */
export type AgenticLoginCompletedState = {
  login_method: string;
  is_new_user: boolean;
};

export const AGENTIC_ANALYTICS_EVENTS = {
  LOGIN_STARTED: "Agentic Login Started",
  LOGIN_COMPLETED: "Agentic Login Completed",
  DASHBOARD_ERROR: "Agentic Dashboard Error",
  CLI_PAIRED: "Agentic CLI Paired",
  APPROVAL_VIEWED: "Agentic Approval Viewed",
  APPROVAL_CTA_CLICKED: "Agentic Approval CTA Clicked",
};

export const AGENTIC_DASHBOARD_ERROR_TYPES = {
  ACCESS_DENIED: "access_denied",
  BOOTSTRAP_FAILED: "bootstrap_failed",
} as const;

export const AGENTIC_LOGIN_METHOD = {
  GOOGLE: "google",
  EMAIL: "email",
  QR: "qr",
  OTHER: "other",
} as const;

export function toAgenticLoginMethod(authConnection?: string): string {
  if (authConnection === "google") return AGENTIC_LOGIN_METHOD.GOOGLE;
  if (authConnection === "email_passwordless") return AGENTIC_LOGIN_METHOD.EMAIL;
  if (authConnection === AGENTIC_LOGIN_METHOD.QR || authConnection === "custom") return AGENTIC_LOGIN_METHOD.QR;
  return AGENTIC_LOGIN_METHOD.OTHER;
}

export const ORGANIZATION_ANALYTICS_EVENTS = {
  // organization
  ORGANIZATION_CREATED: "Organization Created",
  ORGANIZATION_UPDATED: "Organization Updated",
  ORGANIZATION_DELETED: "Organization Deleted",
  ORGANIZATION_OWNER_TRANSFERRED: "Organization Owner Transferred",
  // billing
  PLAN_CHANGE_COMPLETED: "Plan Change Completed",
  PLAN_CHANGE_FAILED: "Plan Change Failed",
  PLAN_CHANGE_CANCEL_COMPLETED: "Plan Change Cancel Completed",
  PLAN_CHANGE_CANCEL_FAILED: "Plan Change Cancel Failed",
  CARD_CHANGE_COMPLETED: "Card Change Completed",
  CARD_CHANGE_FAILED: "Card Change Failed",
  // member
  MEMBER_INVITE_SENT: "Member Invite Sent",
  MEMBER_INVITE_CANCELLED: "Member Invite Cancelled",
  MEMBER_INVITE_REJECTED: "Member Invite Rejected",
  MEMBER_REMOVED: "Member Removed",
  MEMBER_JOINED: "Member Joined",
  MEMBER_LEFT: "Member Left",
  MEMBER_ROLE_UPDATED: "Member Role Updated",
  // CTA
  CTA_CLICKED: "CTA Clicked",
};
